import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useEntitlement } from "@/lib/entitlement";
import { isOwner } from "@/lib/featureAccess";
import { usePaddle } from "@/hooks/usePaddle";
import { PRICING, TRIAL_COPY, fetchFounding500Status, formatFoundingRemaining, trackBillingEvent } from "@/lib/billingClient";
import "./Subscribe.css";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Plan + cadence → display copy and Paddle price key.
const PLAN_DEFS = {
  pro: {
    label: "RigSentinel Pro",
    trial: true,
    prices: {
      monthly: { label: PRICING.pro.monthly.label, priceKey: "field_pro_monthly" },
      annual:  { label: `${PRICING.pro.annual.label} (save ${PRICING.pro.annual.savings})`, priceKey: "field_pro_annual" },
    },
  },
  enterprise: {
    label: "RigSentinel Enterprise",
    trial: false,
    prices: {
      monthly: { label: PRICING.enterprise.monthly.label, priceKey: "professional_monthly" },
      annual:  { label: `${PRICING.enterprise.annual.label} (save ${PRICING.enterprise.annual.savings})`, priceKey: "professional_annual" },
    },
  },
};

export default function Subscribe() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const ent = useEntitlement();
  const paddle = usePaddle();
  const [busy, setBusy] = useState(false);
  const [founding, setFounding] = useState(null);      // /api/founding500/status
  const [foundingLost, setFoundingLost] = useState(false); // exhausted mid-flow
  const completedRef = useRef(false);

  const plan = PLAN_DEFS[params.get("plan")] ? params.get("plan") : "pro";
  const billing = params.get("billing") === "annual" ? "annual" : "monthly";
  const def = PLAN_DEFS[plan];
  const priceDef = def.prices[billing];
  const billingLabel = billing === "annual" ? "Annual" : "Monthly";

  const foundingActive = plan === "pro" && !foundingLost && Boolean(founding?.offer_active);
  const recurring = plan === "pro"
    ? (foundingActive ? PRICING.founding[billing].label : PRICING.pro[billing].label)
    : PRICING.enterprise[billing].label;

  useEffect(() => {
    trackBillingEvent("plan_selected", { plan, cadence: billing });
    if (plan === "pro") fetchFounding500Status(false).then(setFounding);
  }, [plan, billing]);

  // Paddle checkout lifecycle → success / cancel routes.
  useEffect(() => {
    const handler = (e) => {
      const name = (e.detail && e.detail.name) || "";
      if (name === "checkout.completed") {
        completedRef.current = true;
        trackBillingEvent("checkout_completed", { plan, cadence: billing, founding: foundingActive, result: "completed" });
        setTimeout(() => nav(`/subscription/success?plan=${plan}&billing=${billing}`), 1500);
      } else if (name === "checkout.closed" && !completedRef.current) {
        setBusy(false);
        trackBillingEvent("checkout_abandoned", { plan, cadence: billing, founding: foundingActive, result: "closed" });
        nav(`/subscription/cancel?plan=${plan}&billing=${billing}`);
      } else if (name === "checkout.error" && !completedRef.current) {
        // Discount rejected (e.g. Founding allocation just exhausted) or any
        // open failure — NEVER silently charge standard price after showing
        // the discounted price. Fall back to explicit standard-price confirm.
        setBusy(false);
        if (foundingActive) {
          setFoundingLost(true);
          toast.error("The Founding 500 offer is no longer available. Please confirm standard pricing to continue.");
        }
      }
    };
    window.addEventListener("rsc:paddle-event", handler);
    return () => window.removeEventListener("rsc:paddle-event", handler);
  }, [nav, plan, billing, foundingActive]);

  const proceedToCheckout = async (forceStandard = false) => {
    if (!ent.authenticated) {
      toast.message("Sign in required", { description: "Sign in with your email to subscribe." });
      nav(`/auth?returnTo=${encodeURIComponent(`/subscribe?plan=${plan}&billing=${billing}`)}`);
      return;
    }
    const priceId = paddle.priceIds[priceDef.priceKey] || "";
    if (!paddle.configured || !priceId) {
      toast.error("Checkout is not available right now. Please try again later.");
      return;
    }
    setBusy(true);
    try {
      // Founding eligibility: re-check FRESH server-side data right before
      // checkout so a stale cached count can't promise an exhausted offer.
      let applyFounding = false;
      if (plan === "pro" && !forceStandard && !foundingLost) {
        const fresh = await fetchFounding500Status(true);
        if (fresh) setFounding(fresh);
        if (fresh?.offer_active) {
          applyFounding = true;
        } else if (foundingActive) {
          // Offer vanished between page display and checkout — stop and ask.
          setBusy(false);
          setFoundingLost(true);
          return;
        }
      }

      // Identity binding: pass the authenticated app userId via customData so
      // the Paddle webhook credits the signed-in user, not the billing email.
      let appUserId = "";
      let appEmail = ent.email || "";
      try {
        const meRes = await fetch(`${API_BASE}/api/auth/me`, { credentials: "include" });
        if (meRes.ok) {
          const me = await meRes.json();
          if (me && typeof me.userId === "string" && me.userId) appUserId = me.userId;
          if (me && typeof me.email === "string" && me.email) appEmail = me.email;
        }
      } catch (_) { /* webhook falls back to billing email */ }

      trackBillingEvent("checkout_opened", { plan, cadence: billing, founding: applyFounding });
      if (applyFounding) trackBillingEvent("founding500_checkout_started", { plan, cadence: billing, founding: true });

      const opened = await paddle.openCheckout(priceId, {
        customerEmail: ent.email || undefined,
        successUrl: `${window.location.origin}/subscription/success?plan=${plan}&billing=${billing}`,
        customData: appUserId && appEmail ? { appUserId, appEmail } : undefined,
        discountId: applyFounding ? "dsc_01kze1s737sd68qegkj1jyzp1a" : undefined,
      });
      if (!opened) {
        setBusy(false);
        toast.error("Could not open checkout. Please try again.");
      }
    } catch (_) {
      setBusy(false);
      toast.error("Could not open checkout. Please try again.");
    }
  };

  if (isOwner(ent)) {
    return (
      <div data-testid="subscribe-owner" className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full rounded-2xl border border-emerald-500/30 bg-neutral-900 p-8 text-center rsc-subscribe-card">
          <div className="text-4xl mb-3">✅</div>
          <h1 className="text-2xl font-semibold mb-3">You already have full access as the owner.</h1>
          <p className="text-neutral-400 text-sm mb-6">All Pro and Enterprise features are unlocked on this account.</p>
          <button
            type="button"
            data-testid="subscribe-owner-back"
            onClick={() => nav("/")}
            className="w-full rounded-lg bg-emerald-500 hover:bg-emerald-400 text-neutral-950 font-semibold py-3 transition"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="subscribe-page" className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full rounded-2xl border border-amber-500/30 bg-neutral-900 p-8 rsc-subscribe-card">
        <div className="text-center mb-6">
          <div className="text-4xl mb-3">🔒</div>
          <h1 className="text-2xl font-semibold" data-testid="subscribe-plan-heading">{def.label} Subscription</h1>
        </div>

        {foundingActive && (
          <div data-testid="subscribe-founding-banner" className="mb-4 rounded-lg border border-amber-400/50 bg-amber-500/10 px-3 py-2 text-center">
            <div className="text-[11px] font-bold tracking-[0.18em] text-amber-400">FOUNDING 500 — 50% OFF APPLIED</div>
            <div className="text-xs text-neutral-300 mt-0.5" aria-live="polite">{formatFoundingRemaining(founding.remaining)}</div>
          </div>
        )}
        {foundingLost && plan === "pro" && (
          <div data-testid="subscribe-founding-lost" className="mb-4 rounded-lg border border-red-400/50 bg-red-500/10 px-3 py-2 text-center text-xs text-red-200">
            The Founding 500 allocation has just been exhausted. Standard pricing
            ({PRICING.pro[billing].label}) now applies — please confirm below before checkout.
          </div>
        )}

        <div className="space-y-2 text-sm mb-4">
          <div className="flex justify-between border-b border-neutral-800 pb-2">
            <span className="text-neutral-400">Selected Plan</span>
            <span className="font-medium" data-testid="subscribe-plan-label">{def.label}</span>
          </div>
          <div className="flex justify-between border-b border-neutral-800 pb-2">
            <span className="text-neutral-400">Billing</span>
            <span className="font-medium" data-testid="subscribe-billing-label">{billingLabel}</span>
          </div>
          {def.trial && (
            <div className="flex justify-between border-b border-neutral-800 pb-2">
              <span className="text-neutral-400">Today</span>
              <span className="font-semibold text-emerald-400" data-testid="subscribe-trial-label">7 days free</span>
            </div>
          )}
          <div className="flex justify-between pb-2">
            <span className="text-neutral-400">{def.trial ? "Then" : "Price"}</span>
            <span className="font-semibold" data-testid="subscribe-price-label">
              {recurring}
              {foundingActive && (
                <span className="ml-2 text-xs text-neutral-500 line-through">{PRICING.pro[billing].label}</span>
              )}
            </span>
          </div>
        </div>

        {def.trial && (
          <p className="text-xs text-neutral-400 mb-5 leading-relaxed" data-testid="subscribe-trial-disclosure">
            {TRIAL_COPY}
          </p>
        )}

        <div className="flex flex-col gap-3">
          <button
            type="button"
            data-testid="subscribe-proceed-checkout"
            onClick={() => proceedToCheckout(foundingLost)}
            disabled={busy}
            className="w-full rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-60 disabled:cursor-not-allowed text-neutral-950 font-semibold py-3 transition inline-flex items-center justify-center gap-2"
          >
            {busy && <span className="rsc-subscribe-spinner" aria-hidden="true" />}
            {busy
              ? "Opening secure checkout…"
              : foundingLost
                ? `Confirm Standard Price — ${PRICING.pro[billing].label}`
                : def.trial
                  ? `Start 7-Day Free Trial — then ${recurring}`
                  : `💳 Proceed to Checkout — ${recurring}`}
          </button>
          <p className="text-xs text-neutral-500 text-center" data-testid="subscribe-redirect-note">
            Secure checkout by Paddle. Payment details are required to start the trial.
          </p>
          <button
            type="button"
            data-testid="subscribe-back-to-pricing"
            onClick={() => nav("/pricing")}
            className="w-full rounded-lg border border-neutral-700 bg-neutral-800 hover:bg-neutral-700 text-neutral-100 font-medium py-3 transition"
          >
            Back to Pricing
          </button>
          <p className="text-xs text-neutral-500 text-center italic" data-testid="subscribe-cancel-note">
            *No commitment. Cancel anytime from your account.
          </p>
        </div>
      </div>
    </div>
  );
}
